import { PRODUCT_BASE_PATHS } from '@cast/console-constants';

type Prettify<T> = { [K in keyof T]: T[K] };

type ParseRoute<T extends string> = T extends `${string}:${infer P}/${infer R}`
  ? (P extends `${infer O}?`
      ? Partial<Record<O, string | number>>
      : Record<P, string | number>) &
      ParseRoute<`/${R}`>
  : T extends `${string}:${infer P}`
  ? P extends `${infer O}?`
    ? Partial<Record<O, string | number>>
    : Record<P, string | number>
  : T extends `${string}*`
  ? Record<'*', string | number>
  : unknown;

type ExtractRouteParams<T extends string> = Prettify<ParseRoute<T>>;

const SERVER = 'server/:dbServerId' as const;
const DB = `${SERVER}/db/:databaseId` as const;

export const DboRoute = {
  // Org-level
  ROOT: '',
  OVERVIEW: 'overview',

  // DB server
  SERVER_ROOT: SERVER,
  SERVER_DASHBOARD: `${SERVER}/dashboard`,
  SERVER_HEALTH: `${SERVER}/health`,
  SERVER_ERRORS: `${SERVER}/errors`,
  SERVER_CACHES: `${SERVER}/caches`,
  SERVER_CONNECTIONS: `${SERVER}/connections`,
  SERVER_PERFORMANCE: `${SERVER}/performance`,
  SERVER_ENDPOINTS: `${SERVER}/endpoints`,
  SERVER_AUDIT_LOG: `${SERVER}/audit-log`,
  SERVER_SETTINGS: `${SERVER}/settings`,
  SERVER_SETTINGS_ENDPOINTS: `${SERVER}/settings/endpoints`,

  // Logical DB
  DB_ROOT: DB,
  DB_DASHBOARD: `${DB}/dashboard`,
  DB_PERFORMANCE_ADVISOR: `${DB}/performance-advisor`,
  DB_RECOMMENDATION: `${DB}/performance-advisor/recommendations/:recommendationId`,
  DB_CONNECTIONS: `${DB}/connections`,
  DB_CACHING_OVERVIEW: `${DB}/caching/overview`,
  DB_CACHING_QUERIES: `${DB}/caching/queries`,
  DB_CACHING_QUERY: `${DB}/caching/queries/:templateHash`,
  DB_CACHING_RULES: `${DB}/caching/rules`,
} as const;

export type DboRoute = (typeof DboRoute)[keyof typeof DboRoute];

/**
 * Time-range query params preserved across DBO V2 navigation so the
 * selection on one page survives a sidebar click.
 */
const PERSISTENT_QUERY_PARAMS = [
  'date_from',
  'date_to',
  'date_preset',
  'timezone',
];

export function buildDboRoute<R extends DboRoute>(
  route: R,
  ...args: keyof ExtractRouteParams<R> extends never
    ? [params?: undefined, search?: string]
    : [params: ExtractRouteParams<R>, search?: string]
): string;
export function buildDboRoute<R extends DboRoute>(
  route: DboRoute extends R ? R : never,
  params: Record<string, string | number>,
  search?: string
): string;
export function buildDboRoute(
  route: DboRoute,
  params?: Record<string, string | number>,
  search?: string
): string {
  let path: string = route;
  if (params) {
    for (const [key, value] of Object.entries(params)) {
      path = path.replace(`:${key}`, encodeURIComponent(String(value)));
    }
  }

  const baseUrl = path
    ? `${PRODUCT_BASE_PATHS.dbo}/${path}`
    : PRODUCT_BASE_PATHS.dbo;

  const queryParams = new URLSearchParams(
    search?.startsWith('?') ? search.slice(1) : search ?? ''
  );

  new URLSearchParams(window.location.search).forEach((value, key) => {
    if (PERSISTENT_QUERY_PARAMS.includes(key) && !queryParams.has(key)) {
      queryParams.append(key, value);
    }
  });

  const serialized = queryParams.toString();
  return serialized ? `${baseUrl}?${serialized}` : baseUrl;
}
